import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModelInfo } from '../../types/agent';
import { Flag } from './Flag';

/** How dear a model is, as one or two euro signs out of two: the unearned one is greyed out. */
function PriceTier({ tier }: { tier?: number }) {
  const { t } = useTranslation();
  if (!tier) return null;
  const label = t(`model.price${tier}`);
  return (
    <span title={label} aria-label={label} className="font-mono text-[11px] tracking-[-0.5px]">
      <span className="text-muted">{'€'.repeat(tier)}</span>
      <span className="text-line">{'€'.repeat(Math.max(0, 2 - tier))}</span>
    </span>
  );
}

/** Where a model's data is processed, as flags ("EU" then the country). */
function DataFlags({ codes }: { codes: string[] }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {codes.map((c) => <Flag key={c} code={c} />)}
    </span>
  );
}

/** One model as the picker and the workbook intro list it: label and price, who hosts it and where its data goes,
 *  and who made it when that is someone else. */
export function ModelDetails({ model: m, checked }: { model: ModelInfo; checked: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <span className="flex w-full items-center justify-between text-[12px] text-ink">
        {m.label}
        <span className="inline-flex items-center gap-1.5">
          <PriceTier tier={m.price_tier} />
          {checked && <span className="text-accent">✓</span>}
        </span>
      </span>
      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] leading-snug text-faint">
        <span className="inline-flex items-center gap-1">{t('model.hostedBy', { host: m.host })} <Flag code={m.host_country} /></span>
        <span className="inline-flex items-center gap-1">· {t('model.dataIn')} <DataFlags codes={m.data_location} /></span>
      </span>
      {m.maker !== m.host && <span className="text-[11px] leading-snug text-faint">{t('model.madeBy', { maker: m.maker })}</span>}
    </>
  );
}

/** The model picker (PLAN-model-choice.md), next to the mode in the composer: a pill showing the model and where its
 *  data goes, opening a popover with each model's host and data location. A model the organization refuses is not
 *  listed; the pill still names it when it is the model of the open conversation. A conversation keeps its model
 *  (D1): in a started one, picking another starts a new conversation. */
export function ModelSwitch({ models, value, onChange, started, disabled }: { models: ModelInfo[]; value: string | null; onChange: (id: string) => void; /** The open conversation has a model already. */ started: boolean; disabled?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = models.find((m) => m.id === value);
  if (!models.length || !current) return null;

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={t('model.hint', { model: current.label, host: current.host })}
        aria-label={t('model.label')}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-[22px] min-w-0 items-center gap-1 rounded-[5px] border px-1.5 font-mono text-[11px] transition-colors hover:bg-hover-soft disabled:opacity-50 ${open ? 'border-line bg-hover-soft text-ink' : 'border-line-soft text-muted'}`}
      >
        <Flag code={current.data_location[current.data_location.length - 1] ?? current.host_country} />
        <span className="truncate">{current.label}</span>
        <span className="text-[9px] text-faint">▴</span>
      </button>
      {open && (
        <div role="listbox" aria-label={t('model.label')} className="absolute bottom-[calc(100%+6px)] left-0 z-20 flex w-[248px] animate-pc-in-fast flex-col rounded-md border border-line bg-white p-1 shadow-[0_6px_20px_rgba(0,0,0,.08)]">
          {started && <div className="px-2 pb-1 pt-1 text-[11px] leading-snug text-faint">{t('model.startsNew')}</div>}
          {models.filter((m) => m.allowed).map((m) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={m.id === value}
              onClick={() => {
                setOpen(false);
                if (m.id !== value) onChange(m.id);
              }}
              className={`flex flex-col items-start gap-0.5 rounded-sm px-2 py-1.5 text-left hover:bg-hover ${m.id === value ? 'bg-hover' : ''}`}
            >
              <ModelDetails model={m} checked={m.id === value} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
